import { useEffect, useState } from 'react';

/** Matches the grid-template-rows transition in global.css. */
const OPEN_DURATION_MS = 220;

export interface IRevealSlot {
    /** Whether the content is shown. */
    open: boolean;
    children: React.ReactNode;
}

export const testId = 'revealSlot';

/**
 * Animates a conditional field in and out instead of letting it pop.
 *
 * A field that mounts on an answer shoves everything below it down a whole
 * row in one frame, which reads as the screen jumping. This grows the row
 * instead, using the same 0fr/1fr grid technique as the error message slot.
 *
 * The children stay mounted so the closing animation has something to
 * collapse. visibility is what takes them out of the tab order and the
 * accessibility tree while closed, so nothing hidden can still be focused.
 *
 * Clipping is held for the length of the opening animation and then released.
 * It has to be on while the row grows, or the content spills out over whatever
 * sits below; but a focus ring is painted outside the field it belongs to, so
 * a slot that stays clipped cuts the ring off at its own edges — which is
 * exactly where a revealed field sits. Closing clips again immediately, since
 * the collapse needs masking from its first frame.
 */
export function RevealSlot({ open, children }: IRevealSlot): JSX.Element {
    const [clipped, setClipped] = useState(true);

    useEffect(() => {
        if (!open) {
            setClipped(true);
            return undefined;
        }
        const timer = window.setTimeout(
            () => setClipped(false),
            OPEN_DURATION_MS
        );
        return () => window.clearTimeout(timer);
    }, [open]);

    return (
        <div
            data-testid={testId}
            className={`revealSlot ${open ? 'revealSlotOpen' : ''}`}
        >
            <div className={clipped ? 'revealSlotClipped' : ''}>{children}</div>
        </div>
    );
}
